import {Injectable} from "@angular/core";
import {RequesterService} from "../shared/services/requester/requester.service";
import {HttpClient, HttpHeaders} from "@angular/common/http";
import {Store} from "@ngrx/store";
import * as fromStore from "../store";
import {DbStoreState} from "../store";
import {BehaviorSubject, combineLatest, EMPTY, Observable} from "rxjs";
import {catchError, map, tap} from "rxjs/operators";
import {
  BuilderGeneratedPageI,
  PageThemeI,
  SecondaryPageReqI,
  SecondaryPageTypeReqI,
  SiteGenerationI,
  SiteGenerationMobileI,
  SiteGenerationVibeCodingI
} from "./builder-v2.interface";
import {Config} from "../config/config";
import {BuilderSectionsInteractionService} from "./builder-sections-interaction.service";
import {ActivityAction} from "../activities/activity";
import {DbResponse} from "../shared/interceptors/db-response";
import {CookieService} from "ngx-cookie";
import {CommonBuilderDataService} from "./services/common-builder-data.service";
import {isArray} from "chart.js/helpers";

@Injectable({
  providedIn: 'root'
})
export class BuilderV2Service {
  private _ownerWorkspaceId: number;
  private _currentWorkspaceId: number;
  isTier1Country: boolean = false;
  customPostTypes = [];


  constructor(private _requester: RequesterService,
              private _httpClient: HttpClient,
              private _sectionsInteractionsService: BuilderSectionsInteractionService,
              private _commonDataService: CommonBuilderDataService,
              private _store: Store<DbStoreState>) {
    this._getWorkspaceEntities();
  }

  getBuilderUniqueId(): Observable<any> {
    let url = `ai2/start_generation_session`;

    return this._requester.aiAssistant
      .post(url);
  }

  getSiteData(domainId: number, uniqueId?: string) {
    domainId === Config.demoDomainId && (domainId = null);
    let url = `ai2/workspaces/${this._currentWorkspaceId}/domains/${domainId}/get-user-input-wp-data`;
    return this._requester.aiAssistant
      .get(url, {uniqueId: uniqueId}, true);
  }

  getSEOMetaData(domainId: number, uniqueId: string, data) {
    domainId === Config.demoDomainId && (domainId = null);
    const url = this._currentWorkspaceId ? `ai2/workspaces/${this._currentWorkspaceId}/seo_meta` : `ai2/seo_meta`;
    data.tier1 = this.isTier1Country;
    const _data = {params: JSON.stringify(data)};
    domainId && (_data['domainId'] = domainId);
    uniqueId && (_data['uniqueId'] = uniqueId);
    return this._requester.aiAssistant
      .post(url, null, _data);
  }


  public generateAiSectionBuilderDesc(description: string, token, industryName?, websiteType?, image_url?, fromTemplate = false): BehaviorSubject<DbResponse> {
    const data: any = {
      captcha_token: token,
      initial_description: description,
      dev: true,
      is_template_flow: fromTemplate,
      ...(industryName && {industry_name: industryName}),
      ...(websiteType && {website_type: websiteType}),
    };
    /*if (theme_10web_website_generator.industry) {
      form_data.industry_name = theme_10web_website_generator.industry;
    }
    if (theme_10web_website_generator.website_type) {
      form_data.website_type = theme_10web_website_generator.website_type;
    }*/
    return <BehaviorSubject<any>>(this._requester.aiBuilder
      .post(`onboarding/site_description_generator`, null, data));
  }

  public generateAiBuilderDesc(description: string, images?): BehaviorSubject<DbResponse> {
    const data: any = {
      description
    };
    if (images) {
      const filteredImages = JSON.parse(JSON.stringify(images));
      if (filteredImages?.at(0) && filteredImages?.at(0).filename) {
        filteredImages[0] = {url: filteredImages?.at(0).url};
      }
      data["image_files"] = filteredImages;
    }
    return <BehaviorSubject<any>>(this._requester.vibeCoding
      .post(`/discover-business/`, null, data));
  }


  aiGenerateOutline(
    data,
    domainId: number,
    uniqueId?: string,
    useResponse = true,
    builderType?: 'wvc'|'section_based'
  ): Observable<{
    success: boolean;
    message: string;
    data: BuilderGeneratedPageI;
  }> {
    // Handle demo domain
    if (domainId === Config.demoDomainId) domainId = null;

    const isBasic = builderType ? builderType === 'wvc' : this._commonDataService.combinedOutline;
    const hasWorkspace = !!this._currentWorkspaceId;

    // Define URL and requester
    const url = isBasic
      ? '/generate-outline/'
      : hasWorkspace
        ? `ai2/workspaces/${this._currentWorkspaceId}/outline`
        : 'ai2/outline';
    const requester = isBasic
      ? this._requester.vibeCoding
      : this._requester.aiAssistant;

    // Prepare payload
    data.tier1 = this.isTier1Country;
    let _data: Record<string, any> = {params: JSON.stringify(data)};
    domainId && (_data['domainId'] = domainId);
    uniqueId && (_data['uniqueId'] = uniqueId);

    if (isBasic) {
      _data = {description: data.business_description, website_type: data.website_type, experiment_mode: data.experiment_mode || 'a'};
    }

    if (data.analysis) {
      _data = {
        ..._data,
        analysis: data.analysis,
      };
    }

    // Execute request
    return requester.post(url, null, _data).pipe(
      tap((response) => {
        if (!useResponse) return;
        this._sectionsInteractionsService.requestSubject.next({
          action: ActivityAction.BUILDER_GET_OUTLINE,
          response,
        });
      })
    );
  }

  public storeInitialMessage(websiteId: number, clientId: number, initialMessage: string, imageFiles?: any[], useWebsiteGenerationPlanner = false): BehaviorSubject<DbResponse> {
    const data: any = {
      "initial_message": initialMessage || "",
      "image_files": imageFiles || [],
      "domain_id": websiteId,
      "workspace_id": this._currentWorkspaceId,
      "client_id": clientId
    };

    // The planner v2 flow is only requested for plans that support it
    if (useWebsiteGenerationPlanner) {
      data["website_generation_planner_v2"] = 1;
    }

    return <BehaviorSubject<DbResponse>>(this._requester.vibeCoding
        .post(`/workspaces/${this._currentWorkspaceId}/domains/${websiteId}/store-initial-message/`, null, data)
        .pipe(map((response: DbResponse): DbResponse => {
          return response;
        })));
  }


  aiGeneratePageMeta(data, uniqueId?: string): Observable<{
    success: boolean,
    message: string,
    data: BuilderGeneratedPageI
  }> {
    const url = this._currentWorkspaceId ? `ai2/workspaces/${this._currentWorkspaceId}/pages_meta` : `ai2/pages_meta`;
    data.tier1 = this.isTier1Country;
    const _data = {params: JSON.stringify(data)};
    uniqueId && (_data['uniqueId'] = uniqueId);
    return this._requester.aiAssistant
      .post(url, null, _data);
  }

  aiGenerateStyles(data, domainId: number, uniqueId?: string, useResponse = true): Observable<{
    success: boolean,
    message: string,
    data: PageThemeI
  }> {
    domainId === Config.demoDomainId && (domainId = null);
    data.tier1 = this.isTier1Country;
    const _data = {params: JSON.stringify(data)};
    domainId && (_data['domainId'] = domainId);
    uniqueId && (_data['uniqueId'] = uniqueId);
    const url = this._currentWorkspaceId ? `ai2/workspaces/${this._currentWorkspaceId}/styles` : `ai2/styles`;
    return this._requester.aiAssistant
      .post(url, null, _data)
      .pipe(tap((response) => {
        if (useResponse) {
          this._sectionsInteractionsService.requestSubject.next({action: ActivityAction.BUILDER_STYLES, response});
        }
      }));
  }

  aiModifySectionTypes(data, domainId: number, uniqueId?: string, useService = true, initial = false): Observable<any> {
    domainId === Config.demoDomainId && (domainId = null);
    data.tier1 = this.isTier1Country;
    const _data = {params: JSON.stringify(data)};
    domainId && (_data['domainId'] = domainId);
    uniqueId && (_data['uniqueId'] = uniqueId);
    if (this._commonDataService.combinedOutline) {
      const _data = {params: JSON.stringify({...data, custom_post_types: this.customPostTypes})};
      domainId && (_data['domainId'] = domainId);
      uniqueId && (_data['uniqueId'] = uniqueId);
      const url = `/outline/section_types/`;
      return this._requester.vibeCoding
        .post(url, null, _data).pipe(tap((response) => {
          if (response?.body?.data && !isArray(response.body.data)) {
            response.body.data = [response.body.data];
          }
          if (!response || !useService) {
            return;
          }

          if (initial) {

            response.body.data.at(0).saveInState = true;
          }
          this._sectionsInteractionsService.requestSubject.next({
            action: ActivityAction.BUILDER_SECTION_TYPES,
            response: response.body
          });
        }));
    }
    const url = this._currentWorkspaceId ? `ai2/workspaces/${this._currentWorkspaceId}/section_types` : `ai2/section_types`;
    return this._requester.aiAssistant
      .post(url, null, _data).pipe(tap((response) => {
        if (!response || !useService) {
          return;
        }

        if (initial) {
          response.body.data.at(0).saveInState = true;
        }
        this._sectionsInteractionsService.requestSubject.next({
          action: ActivityAction.BUILDER_SECTION_TYPES,
          response: response.body
        });
      }));
  }

  aiModifyColors(data, uniqueId?: string): Observable<any> {
    const url = this._currentWorkspaceId ? `ai2/workspaces/${this._currentWorkspaceId}/colors_modify` : `ai2/colors_modify`;
    return this._requester.aiAssistant
      .post(url, null, {params: JSON.stringify(data), uniqueId: uniqueId});
  }

  public generateProducts(
    data: any,
    domainId: number,
    uniqueId?: string
  ): Observable<any> {

    // Set domainId to null if it matches the demoDomainId
    domainId === Config.demoDomainId && (domainId = null);
    data.tier1 = this.isTier1Country;
    // Prepare _data with JSON-stringified params
    const _data = {params: JSON.stringify(data)};

    // Conditionally add domainId and uniqueId if they are present
    domainId && (_data['domainId'] = domainId);
    uniqueId && (_data['uniqueId'] = uniqueId);

    // Define URL based on the workspace ID
    const url = this._currentWorkspaceId
      ? `ai2/workspaces/${this._currentWorkspaceId}/ecom_data`
      : `ai2/ecom_data`;

    // Make the request and handle response
    return this._requester.aiAssistant
      .post(url, null, _data)
      .pipe(
        tap((response) => {
          // Check for response and useService flag before proceeding
          if (!response) {
            return;
          }

          // Update the section interaction service with the response
          this._sectionsInteractionsService.requestSubject.next({
            action: ActivityAction.PRODUCTS_GENERATE,
            response: response.body
          });
        })
      );
  }

  aiGenerateSite(data: SiteGenerationI, domainId: number, uniqueId?: string): Observable<any> {
    domainId === Config.demoDomainId && (domainId = null);
    const imageModel = data.image_model || 'pro';
    data['tier1'] = this.isTier1Country;
    data = {...data, image_model: imageModel};
    const _data = {params: JSON.stringify(data)};
    domainId && (_data['domainId'] = domainId);
    uniqueId && (_data['uniqueId'] = uniqueId);
    const url = `ai2/workspaces/${this._currentWorkspaceId}/build_site_from_outline`;
    return this._requester.aiAssistant
      .post(url, null, _data);
  }

  vibeCodingCreate(data: SiteGenerationVibeCodingI, domainId?): Observable<any> {
    data['tier1'] = this.isTier1Country;
    const _data = data;
    const url = `/workspaces/${this._currentWorkspaceId}/create/`;
    domainId && (_data['domain_id'] = domainId);
    return this._requester.vibeCoding
      .post(url, null, _data);
  }

  getVibeCodingLimitations(): Observable<any> {
    const url = `/workspaces/${this._currentWorkspaceId}/limitations/`;
    return this._requester.vibeCoding
        .get(url, null, true);
  }

  getVibeCodingFiles(websiteId: number): Observable<any> {
    const url = `/workspaces/${this._currentWorkspaceId}/domains/${websiteId}/files/`;
    return this._requester.vibeCoding
      .post(url, null, {}, true);
  }

  aiGenerateSiteMobile(data: SiteGenerationMobileI, domainId: number, uniqueId?: string): Observable<any> {
    console.log(data);
    domainId === Config.demoDomainId && (domainId = null);
    const imageModel = data.image_model || 'pro';
    data = {...data, image_model: imageModel};
    data['tier1'] = this.isTier1Country;
    const _data = {params: JSON.stringify(data)};
    domainId && (_data['domainId'] = domainId);
    uniqueId && (_data['uniqueId'] = uniqueId);
    const url = `ai2/workspaces/${this._currentWorkspaceId}/build_site_from_description`;
    return this._requester.aiAssistant
      .post(url, null, _data);
  }

  getBuilderActionData(action: string, domainId: number, uniqueId?: string) {
    domainId === Config.demoDomainId && (domainId = null);
    let url = this._currentWorkspaceId ? `ai2/workspaces/${this._currentWorkspaceId}/get-data` : `ai2/get-data`;
    if (domainId) {
      url += `?domainId=${domainId}`;
    }

    return this._requester.aiAssistant
      .get(url, {action, uniqueId: uniqueId}, true).pipe(
        map(response => ({action, response}))
      );
  }

  getTemplatesList(uniqueId: string) {
    const url = this._currentWorkspaceId ? `proxy/workspaces/${this._currentWorkspaceId}/sections/description` : `proxy/sections/description`;
    const data = {
      uniqueId: uniqueId,
    };
    if (this._commonDataService.combinedOutline) {
      const url = '/outline/section_type_descriptions/';
      return this._requester.vibeCoding
        .get(url, data, true);
    }
    return this._requester.aiAssistant
      .get(url, data, true);
  }

  enhanceSection(data, domainId: number, uniqueId: string, useService = true) {
    domainId === Config.demoDomainId && (domainId = null);
    data.tier1 = this.isTier1Country;
    const _data = {params: JSON.stringify(data)};
    domainId && (_data['domainId'] = domainId);
    uniqueId && (_data['uniqueId'] = uniqueId);
    if (this._commonDataService.combinedOutline) {
      const url = `/outline/section/enhance/`;
      return this._requester.vibeCoding
        .post(url, null, _data).pipe(tap((response) => {
          if (!useService) {
            return;
          }
          this._sectionsInteractionsService.requestSubject.next({
            action: ActivityAction.BUILDER_SECTION_ENHANCE,
            response
          });
        }));
    }
    const url = this._currentWorkspaceId ? `ai2/workspaces/${this._currentWorkspaceId}/outline/section/enhance` : `ai2/outline/section/enhance`;
    return this._requester.aiAssistant
      .post(url, null, _data).pipe(tap((response) => {
        if (!useService) {
          return;
        }
        this._sectionsInteractionsService.requestSubject.next({
          action: ActivityAction.BUILDER_SECTION_ENHANCE,
          response
        });
      }));
  }

  enhanceSectionTitle(data, domainId: number, uniqueId: string, useService = true) {
    domainId === Config.demoDomainId && (domainId = null);
    data.tier1 = this.isTier1Country;
    const _data = {params: JSON.stringify(data)};
    domainId && (_data['domainId'] = domainId);
    uniqueId && (_data['uniqueId'] = uniqueId);
    const url = this._currentWorkspaceId ? `ai2/workspaces/${this._currentWorkspaceId}/outline/section/suggest_title` : `ai2/outline/section/suggest_title`;
    if (this._commonDataService.combinedOutline) {
      const url = `/outline/section/suggest_title/`;
      const _data = {params: JSON.stringify({...data, custom_post_types: this.customPostTypes})};
      domainId && (_data['domainId'] = domainId);
      uniqueId && (_data['uniqueId'] = uniqueId);
      return this._requester.vibeCoding
        .post(url, null, _data).pipe(tap((response) => {
          if (!useService) {
            return;
          }
          this._sectionsInteractionsService.requestSubject.next({
            action: ActivityAction.BUILDER_SECTION_SUGGEST_TITLE,
            response
          });
        }));
    }
    return this._requester.aiAssistant
      .post(url, null, _data).pipe(tap((response) => {
        if (!useService) {
          return;
        }
        this._sectionsInteractionsService.requestSubject.next({
          action: ActivityAction.BUILDER_SECTION_SUGGEST_TITLE,
          response
        });
      }));
  }

  enhanceSectionDescription(data, uniqueId: string, useService = true) {
    const url = this._currentWorkspaceId ? `ai2/workspaces/${this._currentWorkspaceId}/outline/section/suggest_description` : `ai2/outline/section/suggest_description`;
    data.tier1 = this.isTier1Country;
    const _data = {params: JSON.stringify(data)};
    uniqueId && (_data['uniqueId'] = uniqueId);
    if (this._commonDataService.combinedOutline) {
      const _data = {params: JSON.stringify({...data, custom_post_types: this.customPostTypes})};
      uniqueId && (_data['uniqueId'] = uniqueId);
      const url = `/outline/section/suggest_description/`;
      return this._requester.vibeCoding
        .post(url, null, _data).pipe(tap((response) => {
          if (!useService) {
            return;
          }
          this._sectionsInteractionsService.requestSubject.next({
            action: ActivityAction.BUILDER_SECTION_SUGGEST_DESCRIPTION,
            response
          });
        }));
    }
    return this._requester.aiAssistant
      .post(url, null, _data).pipe(tap((response) => {
        if (!useService) {
          return;
        }
        this._sectionsInteractionsService.requestSubject.next({
          action: ActivityAction.BUILDER_SECTION_SUGGEST_DESCRIPTION,
          response
        });
      }));
  }

  generateSection(data, domainId: number, uniqueId: string, useService: boolean = true) {
    domainId === Config.demoDomainId && (domainId = null);
    data.tier1 = this.isTier1Country;
    const _data = {params: JSON.stringify(data)};
    domainId && (_data['domainId'] = domainId);
    uniqueId && (_data['uniqueId'] = uniqueId);

    if (this._commonDataService.combinedOutline) {
      const _data = {params: JSON.stringify({...data, custom_post_types: this.customPostTypes})};
      domainId && (_data['domainId'] = domainId);
      uniqueId && (_data['uniqueId'] = uniqueId);
      const url = `/outline/section/generate_outline_section/`;
      return this._requester.vibeCoding
        .post(url, null, _data).pipe(tap((response) => {
          if (!useService) {
            return;
          }
          this._sectionsInteractionsService.requestSubject.next({
            action: ActivityAction.BUILDER_SECTION_GENERATE,
            response
          });
        }));
    }
    const url = this._currentWorkspaceId ? `ai2/workspaces/${this._currentWorkspaceId}/outline/section/generate` : `ai2/outline/section/generate`;
    return this._requester.aiAssistant
      .post(url, null, _data).pipe(tap((response) => {
        if (!useService) {
          return;
        }
        this._sectionsInteractionsService.requestSubject.next({
          action: ActivityAction.BUILDER_SECTION_GENERATE,
          response
        });
      }));
  }

  generateSinglePage(data, domainId: number, uniqueId: string, useService: boolean = true) {
    domainId === Config.demoDomainId && (domainId = null);
    data.tier1 = this.isTier1Country;
    const _data = {params: JSON.stringify(data)};
    domainId && (_data['domainId'] = domainId);
    uniqueId && (_data['uniqueId'] = uniqueId);
    const url = this._currentWorkspaceId ? `ai2/workspaces/${this._currentWorkspaceId}/build_secondary_page` : `ai2/build_secondary_page`;
    return this._requester.aiAssistant
      .post(url, null, _data);
  }

  getSecondaryPage(data: SecondaryPageReqI, domainId: number, uniqueId: string) {
    domainId === Config.demoDomainId && (domainId = null);
    data['tier1'] = this.isTier1Country;
    const _data = {params: JSON.stringify(data)};
    domainId && (_data['domainId'] = domainId);
    uniqueId && (_data['uniqueId'] = uniqueId);
    if (this._commonDataService.combinedOutline) {
      const _data = {params: JSON.stringify({...data, custom_post_types: this.customPostTypes})};
      domainId && (_data['domainId'] = domainId);
      uniqueId && (_data['uniqueId'] = uniqueId);
      const url = `/outline/generate_outline_page/`;
      return this._requester.vibeCoding
        .post(url, null, _data);
    }
    const url = this._currentWorkspaceId ? `ai2/workspaces/${this._currentWorkspaceId}/secondary_page_outline` : `ai2/secondary_page_outline`;
    return this._requester.aiAssistant
      .post(url, null, _data);
  }

  getSecondaryPageType(data: SecondaryPageTypeReqI, domainId: number, uniqueId: string) {
    domainId === Config.demoDomainId && (domainId = null);
    data['tier1'] = this.isTier1Country;
    const _data = {params: JSON.stringify(data)};
    domainId && (_data['domainId'] = domainId);
    uniqueId && (_data['uniqueId'] = uniqueId);
    if (this._commonDataService.combinedOutline) {
      const _data = {params: JSON.stringify({...data, custom_post_types: this.customPostTypes})};
      domainId && (_data['domainId'] = domainId);
      uniqueId && (_data['uniqueId'] = uniqueId);
      const url = `/outline/pages_types/`;
      return this._requester.vibeCoding
        .post(url, null, _data);
    }
    const url = `ai2/pages_types`;
    return this._requester.aiAssistant
      .post(url, null, _data);
  }

  getHeroSection(data) {
    const url = `ai_mini_products/hero_section_full`;
    return this._requester.miniProducts.post(url, null, data);
  }

  setGenerationFinalize(data, uniqueId: string) {
    const url = `ai2/generation/finalise`;
    return this._requester.aiAssistant
      .post(url, null, {params: JSON.stringify(data), uniqueId: uniqueId});
  }

  getGenerationFinalize(action: 'finalise_generation', uniqueId: string) {
    const url = `ai2/get-user-input-data`;

    return this._requester.aiAssistant
      .get(url, {action, uniqueId: uniqueId}, true)
      .pipe(map((response: DbResponse): DbResponse => {
        return response;
      }));
  }

  getFigmaScreenshot(figma_url: string) {
    return this._requester.vibeCoding.post('/figma-screenshot/', null, {figma_url});
  }

  getAWS() {
    let httpHeaders: HttpHeaders = new HttpHeaders({
      'Accept': `application/x.${Config.accept}wvc.v1+json`,
      'Authorization': `Service ${Config.wvcAcceptToken}`
    });

    return this._httpClient.get(`${Config.vibeCodingEndpoint}/sts-credentials/`, {headers: httpHeaders}).pipe(
      tap(resp => console.log('tap', resp)),
      catchError(err => {
        console.error('HTTP error', err);
        return EMPTY;
      })
    );
  }

  configureWvc(domainId: number, builderType: 'wvc' | 'section_based', websiteType: string): Observable<any> {
    domainId === Config.demoDomainId && (domainId = null);
    const url = `workspaces/${this._currentWorkspaceId}/domains/${domainId}/wvc-configure`;
    const data = { builder_type: builderType};
    websiteType && (data['website_type'] = websiteType);
    return this._requester.lxd.post(url, null, data);
  }

  private _getWorkspaceEntities(): void {
    combineLatest(
      this._store.select(fromStore.getUserSubscription),
      this._store.select(fromStore.getWorkspaceIdEntities)
    ).subscribe(([userSubscription, workspaceIdEntities]) => {
      this._ownerWorkspaceId = workspaceIdEntities.ownerWorkspaceId;
      this._currentWorkspaceId = workspaceIdEntities.currentWorkspaceId || this._ownerWorkspaceId;
    });
  }

  getOngoingGenerationStatus(websiteId: number): Observable<any> {
    const url = `/workspaces/${this._currentWorkspaceId}/domains/${websiteId}/generation-status/`;
    return this._requester.vibeCoding
        .get(url, null, true);
  }
}
